import { useState } from "react";
import { Link } from "react-router-dom";
import { apiClient } from "../lib/api";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { CheckCircle2 } from "lucide-react";

export default function RSVP() {
  const [status, setStatus] = useState("yes");
  const [lodging, setLodging] = useState("villa_taina");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    guest_name: "",
    partner_name: "",
    email: "",
    dietary: "",
    notes: "",
  });

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.guest_name.trim() || !form.email.trim()) {
      toast.error("Name and email are required.");
      return;
    }
    setSubmitting(true);
    try {
      await apiClient.post("/rsvp", {
        ...form,
        status,
        lodging,
      });
      setSubmitted(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send — try again?");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center py-24 px-6" data-testid="rsvp-success">
        <div className="max-w-xl text-center">
          <CheckCircle2 className="w-16 h-16 text-sage-dark mx-auto mb-6" />
          <p className="text-xs tracking-[0.4em] uppercase text-terracotta font-medium mb-4">
            {status === "yes" ? "You're in" : status === "no" ? "Got it" : "On the fence"}
          </p>
          <h1 className="font-display text-5xl md:text-7xl font-black text-ink leading-[0.95] mb-6">
            {status === "yes" ? "See you in Cabarete." : status === "no" ? "We'll miss you." : "Let us know soon."}
          </h1>
          <p className="text-muted_ink text-lg mb-8">
            {status === "yes"
              ? "I'll be in touch about rooms and details. Next move: grab your room before it's gone. Heads up — room claims require payment up front (Venmo/PayPal/Revolut) to lock in your reservation."
              : "Thanks for letting me know. I'll be in touch."}
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/" className="btn-ghost" data-testid="rsvp-home-btn">Back home</Link>
            {status === "yes" && (
              <Link to="/stay" className="btn-primary" data-testid="rsvp-to-stay-btn">Pick a room</Link>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-cream">
      <section className="pt-20 pb-10 md:pt-28 md:pb-14 relative overflow-hidden" data-testid="rsvp-hero">
        <div className="absolute inset-0 hero-glow pointer-events-none" />
        <div className="max-w-4xl mx-auto px-6 md:px-10 relative">
          <p className="text-xs tracking-[0.4em] uppercase text-terracotta font-medium mb-4">RSVP</p>
          <h1 className="font-display text-6xl md:text-8xl font-black text-ink leading-[0.9]">
            Say <span className="italic text-sage-dark">yes.</span>
          </h1>
          <p className="text-lg text-muted_ink mt-5 max-w-xl">
            Takes 45 seconds. You can always update later. Rooms are a separate step — RSVP first, then pick your room.
          </p>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="max-w-2xl mx-auto px-6 md:px-10">
          <form onSubmit={submit} className="space-y-8 bg-sand border border-shell rounded-lg p-6 md:p-10" data-testid="rsvp-form">
            <div className="grid sm:grid-cols-2 gap-5">
              <div className="space-y-2">
                <Label htmlFor="guest_name" className="text-ink font-medium">Your name *</Label>
                <Input id="guest_name" value={form.guest_name} onChange={upd("guest_name")} required data-testid="rsvp-name" className="bg-cream h-12" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="partner_name" className="text-ink font-medium">Partner or +1 (optional)</Label>
                <Input id="partner_name" value={form.partner_name} onChange={upd("partner_name")} data-testid="rsvp-partner" className="bg-cream h-12" />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-ink font-medium">Email *</Label>
              <Input id="email" type="email" value={form.email} onChange={upd("email")} required data-testid="rsvp-email" className="bg-cream h-12" />
            </div>

            <div className="space-y-3">
              <Label className="text-ink font-medium">Are you coming?</Label>
              <RadioGroup value={status} onValueChange={setStatus} className="grid grid-cols-3 gap-3" data-testid="rsvp-status-group">
                {[
                  { v: "yes", l: "Yes, obviously" },
                  { v: "maybe", l: "Maybe" },
                  { v: "no", l: "Can't make it" },
                ].map((o) => (
                  <label
                    key={o.v}
                    htmlFor={`st-${o.v}`}
                    className={`cursor-pointer rounded-full border text-center py-3 px-3 text-sm font-medium transition-all ${
                      status === o.v
                        ? "bg-terracotta text-cream border-terracotta"
                        : "bg-cream text-ink border-shell hover:border-terracotta"
                    }`}
                    data-testid={`rsvp-status-${o.v}`}
                  >
                    <RadioGroupItem value={o.v} id={`st-${o.v}`} className="sr-only" />
                    {o.l}
                  </label>
                ))}
              </RadioGroup>
            </div>

            <div className="space-y-2">
              <Label className="text-ink font-medium">Where are you staying?</Label>
              <Select value={lodging} onValueChange={setLodging}>
                <SelectTrigger className="bg-cream h-12" data-testid="rsvp-lodging">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="villa_taina">Villa Taina (main hotel)</SelectItem>
                  <SelectItem value="other_hotel">Another hotel in Cabarete</SelectItem>
                  <SelectItem value="airbnb">Airbnb / rental</SelectItem>
                  <SelectItem value="tbd">Still figuring it out</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="dietary" className="text-ink font-medium">Dietary restrictions</Label>
              <Input id="dietary" value={form.dietary} onChange={upd("dietary")} placeholder="Vegetarian, allergies, etc." data-testid="rsvp-dietary" className="bg-cream h-12" />
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes" className="text-ink font-medium">Anything else I should know?</Label>
              <Textarea id="notes" value={form.notes} onChange={upd("notes")} rows={4} data-testid="rsvp-notes" className="bg-cream" />
            </div>

            <button type="submit" disabled={submitting} className="btn-primary w-full h-14 text-base" data-testid="rsvp-submit">
              {submitting ? "Sending..." : "Send RSVP"}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
